import { useCallback } from 'react';
import type { Dispatch, MutableRefObject } from 'react';
import type {
  Step,
  Option,
  DCS,
  DCSContext,
  Component,
  DecisionProfileResponse,
  StepResult,
} from '@de/types/types';
import type {
  FlowState,
  DynamicExperienceAction,
} from '../providers/dynamicExperienceReducer';
import { actionCreators } from '../providers/dynamicExperienceReducer';
import { StepType } from '@de/types/enums';
import getNextStep from '@de/utils/getNextStep';
import {
  getAllSelectedOptions,
  getFullOptionsFromFlowState,
  isOptionPii,
} from '@de/utils/dataFromFlowUtils';
import { validateCurrentStep } from '@de/utils/validateStep';
import { resolveEjectUrl } from '@de/utils/generateUrl';
import { buildContextParams } from '@de/utils/buildContextParams';
import type { SiteConfig } from '@de/types/config';
import { translateStepPlaceholders } from '@de/utils/translateStepPlaceholders';
import {
  resolveActionOutcomeForStep,
  ACTION_OUTCOME_RESULTS,
} from '@de/events';
import {
  applyEntryPointDefaultsAtCommit,
  applyProductContext,
  buildFlowContext,
  getEntryPointConfigFromSteps,
  listProductFieldFallbacks,
  resolveConfiguredCustomDimensions,
} from '@de/utils/configurable-step';
import type {
  CustomDimensionMapping,
  ProductFieldMapping,
} from '@de/utils/configurable-step';
import type { ForwardStepEventFirerRef } from '../providers/useForwardStepEventFiring';
import { useTaxonomy } from '@de/providers/TaxonomyProvider';

const SANITIZED_FIELD_VALUE = 'SANITIZED';

/** Build minimal field array from step data for FormContinued payload */
function getFieldsFromStepData(
  step: Step,
  stepData: Record<string, unknown>,
  flowState: FlowState,
  currentStepIndex: number,
): {
  fieldName: string;
  fieldValue: string;
  fieldId?: string;
  fieldNumber?: number;
}[] {
  if (step.type === StepType.MULTI_QUESTION && 'stepQuestions' in step) {
    const childQuestions = (step as any).stepQuestions || [];
    const fields: {
      fieldName: string;
      fieldValue: string;
      fieldId: string;
      fieldNumber: number;
    }[] = [];
    childQuestions.forEach((childQ: any) => {
      const childData = flowState[childQ.id] || {};
      const option = childData.option;
      if (!option) return;

      const fieldName = childQ.stepName || childQ.title || '';
      const fieldId = childQ.id;
      // All children of a multi-question share the same fieldNumber (the parent's step index)
      const fieldNumber = currentStepIndex;

      if (Array.isArray(option)) {
        option.forEach((o: any) => {
          fields.push({
            fieldName,
            fieldId,
            fieldNumber,
            fieldValue: isOptionPii(o, childData.leadFieldAssociated, childQ)
              ? SANITIZED_FIELD_VALUE
              : String(o?.text ?? o?.value ?? ''),
          });
        });
      } else if (typeof option === 'object' && option !== null) {
        const val = (option as any).text ?? (option as any).value;
        fields.push({
          fieldName,
          fieldId,
          fieldNumber,
          fieldValue: isOptionPii(
            option as Option,
            childData.leadFieldAssociated,
            childQ,
          )
            ? SANITIZED_FIELD_VALUE
            : String(val ?? ''),
        });
      }
    });
    return fields;
  }

  if (!stepData || typeof stepData !== 'object') {
    return [];
  }
  const option = stepData.option;
  const fieldName = step.stepName || step.title || '';
  const fieldId = step.id;

  if (Array.isArray(option)) {
    return option.map((o: any) => ({
      fieldName,
      fieldId,
      fieldNumber: currentStepIndex,
      fieldValue: isOptionPii(o, stepData.leadFieldAssociated as string, step)
        ? SANITIZED_FIELD_VALUE
        : String(o?.text ?? o?.value ?? ''),
    }));
  }
  if (option && typeof option === 'object' && option !== null) {
    const val = (option as any).text ?? (option as any).value;
    return [
      {
        fieldName,
        fieldId,
        fieldNumber: currentStepIndex,
        fieldValue: isOptionPii(
          option as Option,
          stepData.leadFieldAssociated as string,
          step,
        )
          ? SANITIZED_FIELD_VALUE
          : String(val ?? ''),
      },
    ];
  }
  return [];
}

interface UseHandleStepForwardParams {
  flowStateRef: React.MutableRefObject<FlowState>;
  currentStep?: Step;
  steps: Step[];
  currentStepIndex: number;
  isLastStep: boolean;
  clearValidationErrors: () => void;
  updateFlowState: (
    stepId: string,
    data: Record<string, unknown> | null,
  ) => void;
  handleSubmit: (result?: Step, redirectUrl?: string) => Promise<void>;
  dispatch: Dispatch<DynamicExperienceAction>;
  dcs?: DCSContext;
  audienceIntent?: DecisionProfileResponse | null;
  /**
   * Ref bridge into `TrackingProvider`'s `useForwardStepEventFiring`.
   * The provider writes its firer into `.current` on mount; this
   * hook calls `.current?.(forwardCtx)` at the firing point so the
   * spec-compliant orchestration of `FormStarted` / `ProductClicked`
   * / `FormContinued` lives in a single place (the provider) per
   * Confluence "Dynamic Experience Eventing Requirements" §6.1 /
   * §6.7. When no `TrackingProvider` is mounted, `.current` is `null`
   * and the forward-click events silently do not fire — matches the
   * legacy se-supersonic standard render path.
   */
  forwardStepEventFirerRef: ForwardStepEventFirerRef;
  /**
   * Shared with the click handler. Set when this click owns the entry
   * `ProductClicked`. Cleared here when validation fails or throws,
   * because that return happens after the click handler has written
   * the ref and before the firer runs. The TCPA gate does not clear it.
   */
  entryProductClickSuppressedRef?: MutableRefObject<boolean>;
  /**
   * DSL-declared `tracking.productFields` map for the current entry
   * step. Each entry maps a wire `ProductClicked.product.*` field to
   * a dotted path against the live `flowContext` — resolved at click
   * time via {@link applyProductContext} and threaded onto
   * `ForwardStepClickContext.productOverride` for the firer.
   *
   * Undefined for legacy `StepType.DCS` entries without a DSL config;
   * the hook then falls back to the hardcoded `degree`/`category`/
   * `subject` triad on `ctx.dcs`.
   */
  entryProductFields?: Readonly<Record<string, ProductFieldMapping>>;
  /**
   * Sibling `tracking.customDimensions` mappings for the current
   * entry step. Resolved against live `flowContext` at click time and
   * passed as `mappedCustomDimensions` on the firer context.
   */
  entryCustomDimensionMappings?: readonly CustomDimensionMapping[];
  // Site config, threaded so the redirecting-entry `outboundUrl` can be resolved
  // by `generateResultUrl`. Optional for the legacy se-supersonic render path.
  siteConfig?: Partial<SiteConfig>;
  // Flow correlation id, ridden on the entry `outboundUrl` so the eject
  // destination ties back to this flow (matches Supersonic).
  correlationId?: string;
  // Threaded so the entry `outboundUrl` is the real resolved destination from
  // the same `generateResultUrl` call nav uses, not the bare redirect sentinel.
  fromStepIndex?: number;
  formViewedCorrelationId?: string;
  /** Allowlisted `customField` names from `entryPoint.forwardFields` (Discover `ctx_*`). */
  forwardFields?: string[];
}

/**
 * The URL a step hands the flow off to instead of advancing, or undefined when
 * it advances normally.
 *
 * A step "ejects" the flow when its CTA hands off to an external URL instead
 * of advancing to a next step. Originally this was DCS-only (legacy
 * `se-supersonic-ui` `useDynamicQdf` behavior); the DSL-backed QDF migration
 * carries the same `redirectUrl` on a DSL-config entry (`dslConfigId` /
 * `dslConfig` present, see `Component` in `types.ts`).
 *
 * DSL-config opt-in: a DSL step participates in exit-on-submit only when its
 * `redirectUrl` contains the `discover` sentinel. This keeps the legacy DCS
 * path unchanged while letting admin authors flip the behavior on per DSL
 * config without a code change. The literal value is treated as a feature
 * flag, not the destination -- the final URL is built by `generateResultUrl`
 * from the resolved DCS data and result step.
 */
function exitingRedirectUrlOf(currentStep: Step): string | undefined {
  const isDslConfigStep = !!(
    (currentStep as Step & Component).dslConfigId ||
    (currentStep as Step & Component).dslConfig
  );
  const dslRedirectUrl = isDslConfigStep
    ? (currentStep as Step & Component).redirectUrl
    : undefined;
  const dslShouldEject =
    !!dslRedirectUrl && dslRedirectUrl.toLowerCase().includes('discover');
  return currentStep.type === StepType.DCS
    ? (currentStep as Step & DCS).redirectUrl
    : dslShouldEject
      ? dslRedirectUrl
      : undefined;
}

/** How a caller of the step-forward handler constrains it. */
export interface StepForwardOptions {
  /**
   * Advance only to another step. When the click would submit the flow (the
   * last step) or hand off to a redirect, nothing runs: no validation, no
   * events, no submission. A partner module's output uses this so a signal it
   * controls cannot submit a lead or leave the page.
   */
  readonly refuseSubmission?: boolean;
}

export function useHandleStepForward({
  flowStateRef,
  currentStep,
  steps,
  currentStepIndex,
  isLastStep,
  clearValidationErrors,
  updateFlowState,
  handleSubmit,
  dispatch,
  dcs,
  audienceIntent,
  forwardStepEventFirerRef,
  entryProductClickSuppressedRef,
  entryProductFields,
  entryCustomDimensionMappings,
  siteConfig,
  correlationId,
  fromStepIndex,
  formViewedCorrelationId,
  forwardFields,
}: UseHandleStepForwardParams) {
  const taxonomy = useTaxonomy();

  return useCallback(
    async (options?: StepForwardOptions) => {
      if (!currentStep) {
        clearValidationErrors();
        return;
      }

      if (
        options?.refuseSubmission === true &&
        (isLastStep || !!exitingRedirectUrlOf(currentStep))
      ) {
        return;
      }

      clearValidationErrors();

      // Run validation before advancing
      try {
        const validationResult = await validateCurrentStep(
          currentStep,
          flowStateRef.current,
        );

        if (!validationResult.isValid) {
          dispatch(actionCreators.setValidationErrors(validationResult.errors));
          if (entryProductClickSuppressedRef) {
            entryProductClickSuppressedRef.current = false;
          }
          return;
        }

        // If ZIP validation resolved location data, update the location entry directly
        if (validationResult.resolvedZipData?.stateAbbr) {
          const { stateAbbr } = validationResult.resolvedZipData;
          const currentLocation = flowStateRef.current['location'] || {};
          updateFlowState('location', {
            ...currentLocation,
            state: stateAbbr,
          });
        }
      } catch {
        // Surface validation API failures as a user-facing error, never crash
        dispatch(
          actionCreators.setValidationErrors([
            {
              field: currentStep.id || 'unknown',
              message:
                'Something went wrong validating your input. Please try again.',
            },
          ]),
        );
        if (entryProductClickSuppressedRef) {
          entryProductClickSuppressedRef.current = false;
        }
        return;
      }

      // Modal gate: if this step has a modal enabled and TCPA not yet accepted, show modal
      const tcpaAccepted = flowStateRef.current['TCPA']?.accepted === true;
      if (currentStep.modal?.enable && !tcpaAccepted) {
        dispatch(actionCreators.setOpenModal(true));
        return;
      }

      const currentStepId = currentStep.id || '';

      // Commit-time entry defaults: fill blank DCS triad dims from authored
      // `entryPoint.defaults` into flowState options before branching /
      // eject / DCS product reads. Does not touch DSM formState (Select
      // placeholders stay). No-op when the toggle is off or dims are set.
      const entryStepId =
        steps.find((step) => step.isEntry === true)?.id ?? steps[0]?.id;
      const entryPointConfig = getEntryPointConfigFromSteps(steps);
      const committedFlowState = applyEntryPointDefaultsAtCommit(
        flowStateRef.current,
        entryStepId,
        entryPointConfig,
        taxonomy.translateDcsValue,
      );
      if (committedFlowState !== flowStateRef.current && entryStepId) {
        const entryData = committedFlowState[entryStepId];
        if (entryData) {
          // Sync the ref for the rest of this handler (branching / eject /
          // DCS reads) before the dispatched UPDATE_FLOW_STATE re-renders.
          flowStateRef.current = committedFlowState;
          updateFlowState(entryStepId, entryData);
        }
      }

      const stepDataWeLeft = flowStateRef.current[currentStepId] || {};
      const field = getFieldsFromStepData(
        currentStep,
        stepDataWeLeft as Record<string, unknown>,
        flowStateRef.current,
        currentStepIndex,
      );

      const exitingRedirectUrl = exitingRedirectUrlOf(currentStep);
      const isExitingStepWithRedirect = !!exitingRedirectUrl;

      // Resolve the user-driven next step up-front so we can:
      //   1. Differentiate `actionOutcome` per CTA on `ProductClicked` (§6.4 of
      //      the Dynamic Experience Eventing Requirements — the per-CTA
      //      outcome depends on whether *this* click leads to a question or
      //      directly to results), and
      //   2. Reuse the resolved step for downstream navigation / submit
      //      (instead of re-running the async resolver later).
      //
      // For the last step we don't compute a next step (the click triggers
      // submit, which by definition resolves to a result outcome).
      const stepData = flowStateRef.current[currentStepId] || {};
      const selectedOptions = getAllSelectedOptions(flowStateRef.current);
      // `getNextStep` is typed to return `Step | null | undefined`; normalize to
      // `Step | undefined` so the helpers below stay strictly-typed.
      const resolvedNextStep: Step | undefined = isLastStep
        ? undefined
        : ((await getNextStep({
            currentStepData: stepData,
            selectedOptions,
            currentStep,
            allSteps: steps,
          })) ?? undefined);
      // Per-CTA outcome: terminal click (submit) navigates straight to results;
      // otherwise we look at the actual next step the user is about to land on.
      const ctaActionOutcome = isLastStep
        ? ACTION_OUTCOME_RESULTS
        : resolveActionOutcomeForStep(resolvedNextStep);
      // Structural source of truth for the entry's FormSubmitted vs FormContinued
      // split (next step is RESULT or terminal), decoupled from `actionOutcome`.
      const isResultsLanding = ctaActionOutcome === ACTION_OUTCOME_RESULTS;

      // Fire forward-click events (`FormStarted` / `ProductClicked` /
      // `FormContinued`) via the `TrackingProvider`-owned firer. The
      // spec-compliant orchestration lives in
      // `useForwardStepEventFiring` per Confluence "Dynamic Experience
      // Eventing Requirements" §6.1 / §6.7 / §6.8 — this hook only
      // gathers the click-time context. `.current` is `null` when no
      // `TrackingProvider` is mounted (standard se-supersonic render
      // path); the optional call silently no-ops in that case.
      //
      // `product.{variant,category,name}` is omitted on non-DCS entries.
      // Two exclusive resolution paths:
      //
      //   1. DSL-backed entry with a `tracking.productFields` map →
      //      resolve against the live `flowContext` via
      //      `applyProductContext` and pass as `productOverride`.
      //   2. Legacy `StepType.DCS` entry without a DSL config →
      //      fall back to the hardcoded `degree`/`category`/`subject`
      //      reads on `ctx.dcs`.
      //
      // Within an entry we let unselected fields fall through as
      // `undefined`; only the submit path substitutes `'general'`.
      const hasEntryProductFields =
        currentStep.isEntry &&
        entryProductFields !== undefined &&
        Object.keys(entryProductFields).length > 0;
      const hasEntryCustomDimensionMappings =
        currentStep.isEntry &&
        entryCustomDimensionMappings !== undefined &&
        entryCustomDimensionMappings.length > 0;

      let productOverride: Record<string, string> | undefined;
      let productFieldFallbacks: string[] | undefined;
      let mappedCustomDimensions:
        | Array<{ key: string; value: string }>
        | undefined;
      let dcsData:
        | {
            degree: string | undefined;
            category: string | undefined;
            subject: string | undefined;
          }
        | undefined;

      if (hasEntryProductFields || hasEntryCustomDimensionMappings) {
        // Rebuild `flowContext` from the live `flowState` rather than
        // threading the bridge's memoized value, since this hook is also
        // mounted on the standalone DSL render path. Resolve the heading
        // fallback once here (same source ConfigurableStep memoizes) so
        // `buildFlowContext` stays free of DSL-config walks.
        const subjectLabelFallback =
          getEntryPointConfigFromSteps(steps)?.subjectLabelFallback;
        const flowContext = buildFlowContext(
          flowStateRef.current,
          steps,
          currentStep,
          { subjectLabelFallback },
        );
        if (hasEntryProductFields) {
          productOverride = applyProductContext({
            map: entryProductFields,
            root: flowContext,
          });
          const missed = listProductFieldFallbacks(
            entryProductFields,
            flowContext,
          );
          if (missed.length > 0) productFieldFallbacks = missed;
        }
        if (hasEntryCustomDimensionMappings) {
          const resolved = resolveConfiguredCustomDimensions(
            entryCustomDimensionMappings,
            flowContext,
          );
          if (resolved.length > 0) {
            mappedCustomDimensions = resolved;
          }
        }
      } else if (currentStep.isEntry) {
        // Any entry step reports the latest flow-state DCS on `ProductClicked`.
        // Only set `dcs` when a selection exists, else stay empty as before.
        const latestDcsData = {
          degree: getFullOptionsFromFlowState('degree', flowStateRef.current)
            ?.value as string | undefined,
          category: getFullOptionsFromFlowState(
            'category',
            flowStateRef.current,
          )?.value as string | undefined,
          subject: getFullOptionsFromFlowState('subject', flowStateRef.current)
            ?.value as string | undefined,
        };

        if (
          latestDcsData.degree ||
          latestDcsData.category ||
          latestDcsData.subject
        ) {
          dcsData = latestDcsData;
        }
      }

      // Inline CTAs emit `outboundUrl: null` to distinguish "no URL" from empty.
      const redirectUrl = (currentStep as Step & DCS).redirectUrl;
      // A redirecting entry must carry the real resolved destination — the same URL
      // nav uses — so we reuse `generateResultUrl` (a pure builder) with the same
      // inputs. Non-entry redirects keep the raw URL; inline CTAs stay `null`.
      let outboundUrl: string | null = redirectUrl || null;
      if (isExitingStepWithRedirect && currentStep.isEntry) {
        const contextParams = buildContextParams(
          flowStateRef.current,
          steps,
          forwardFields,
        );
        const { url } = resolveEjectUrl({
          step: (resolvedNextStep ?? currentStep) as Step & StepResult,
          currentStepId,
          correlationId: correlationId ?? '',
          siteConfig,
          dcsData: {
            degree: getFullOptionsFromFlowState('degree', flowStateRef.current),
            category: getFullOptionsFromFlowState(
              'category',
              flowStateRef.current,
            ),
            subject: getFullOptionsFromFlowState(
              'subject',
              flowStateRef.current,
            ),
          },
          redirectUrl: exitingRedirectUrl,
          fromStepIndex,
          formViewedCorrelationId,
          contextParams,
        });
        outboundUrl = url || null;
      }

      forwardStepEventFirerRef.current?.({
        isExitingStepWithRedirect,
        isLastStep,
        outboundUrl,
        ctaText: currentStep.nextButtonText || 'Next Question',
        actionOutcome: ctaActionOutcome,
        isResultsLanding,
        ...(productOverride && Object.keys(productOverride).length > 0
          ? { productOverride }
          : {}),
        ...(productFieldFallbacks ? { productFieldFallbacks } : {}),
        ...(mappedCustomDimensions ? { mappedCustomDimensions } : {}),
        ...(dcsData ? { dcs: dcsData } : {}),
        field,
      });

      // If this step ejects the flow via redirectUrl, submit instead of navigating.
      // Covers DCS (legacy) and DSL-backed COMPONENT steps (QDF migration).
      if (isExitingStepWithRedirect && exitingRedirectUrl) {
        if (resolvedNextStep) {
          await handleSubmit(resolvedNextStep, exitingRedirectUrl);
        }
        return;
      }

      // If this is the last step, submit
      if (isLastStep) {
        await handleSubmit();
        return;
      }

      if (resolvedNextStep) {
        const newStepIndex = currentStepIndex + 1;
        const translatedNextStep = translateStepPlaceholders(resolvedNextStep, {
          steps: steps,
          flowState: flowStateRef.current,
          dcs,
          audienceIntent: audienceIntent || undefined,
        });

        dispatch(actionCreators.setCurrentStep(translatedNextStep));
        dispatch(actionCreators.setCurrentStepIndex(newStepIndex));
        dispatch(actionCreators.addToNavigationHistory(translatedNextStep));
      }
    },
    [
      currentStep,
      steps,
      currentStepIndex,
      isLastStep,
      clearValidationErrors,
      updateFlowState,
      handleSubmit,
      flowStateRef,
      dispatch,
      dcs,
      audienceIntent,
      forwardStepEventFirerRef,
      entryProductClickSuppressedRef,
      entryProductFields,
      entryCustomDimensionMappings,
      siteConfig,
      correlationId,
      fromStepIndex,
      formViewedCorrelationId,
      forwardFields,
      taxonomy.translateDcsValue,
    ],
  );
}
